import { ClearOutlined, DownOutlined, SearchOutlined, SunOutlined } from "@ant-design/icons";
import { Avatar, Cascader, Divider, Input, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { React, useCallback, useEffect, useMemo, useState } from "react";
import FarmerService from "../../services/Farmer";
import UserService from "../../services/User";
import { useTranslation } from "react-i18next";
import SeasonService from "../../services/Season";
import {crop_phases} from "../../utilities/crop_phases";
import { useLocation, useNavigate, useParams } from "react-router";

const textStyle = {
  fontWeight: "bolder",
}

const SearchToolbar = (props) => {
  const { t, i18n } = useTranslation("translation");
  const [farmers, setFarmers] = useState([]);
  const [users, setUsers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [phases, setPhases] = useState([]);
  const [user_options, setUserOptions] = useState([]);
  const [farmer_options, setFarmerOptions] = useState([]);
  const [season_options, setSeasonOptions] = useState([]);
  const [phase_options, setPhaseOptions] = useState([]);
  const [dropdown_searches, setDropdownSearches] = useState({ user: "", farmer: "", season: "", phase: "" });
  const [search_criteria, setSearchCriteria] = useState(
    props.localSearchCriteria || {user: false, farmer: false, season: false, phase: false, search_query: false}
  );

  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams();
  // read search params -> build initial state
  const { user, farmer, season, field, phase, search_query } = useMemo(
    () => readParams(location.search),
    [location.search]
  );


  function readParams(search) {
    const params = new URLSearchParams(search);
  
    return {
      user:         params.get('user')         ?? null,
      farmer:       params.get('farmer')       ?? null,
      season:       params.get('season')       ?? null,
      field:        params.get('field')        ?? null,
      phase:        params.get('phase')        ?? null,
      search_query: params.get('search_query') ?? '',
    };
  }

  // build a fresh "?user=…&farmer=…" string and push it into the address bar
const writeSearchCriteria = useCallback(
  (criteria) => {
    const params = new URLSearchParams();          // start clean

    if (criteria.user)         params.set('user',   criteria.user.id   ?? criteria.user);
    if (criteria.farmer)       params.set('farmer', criteria.farmer.id ?? criteria.farmer);
    if (criteria.season)       params.set('season', criteria.season.id ?? criteria.season);
    if (criteria.phase)        params.set('phase',  criteria.phase.name.toLowerCase() ?? criteria.phase);
    if (criteria.search_query) params.set('search_query', criteria.search_query);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }                            // ← same as history.replaceState
    );
  },
  [navigate, location.pathname]                    // ‹– never depend on location.search here
);

  const updateSearchCriteria = (criteria) => {
    setSearchCriteria(criteria);

    if(props.onSearchCriteriaChange) {
      props.onSearchCriteriaChange(criteria);
      return;
    }

    writeSearchCriteria(criteria);
  }

  useEffect(() => {
    if(id) {
      console.log("ID: ", id);
      //let _search_criteria = location.pathname.split("/")[0]
      //writeSearchCriteria({_search_criteria: id});
    }
  }, [id])
  useEffect(() => {
    if(props.farmers){ // if farmers cascader is requested from the parent components
      const fetchFarmers = () => {
        FarmerService.getFarmers({season: null, page: "all"}, (_farmers) => {
          setFarmers(_farmers.data);
        });
        
      }
      fetchFarmers();
    }

    if(props.users){
      const fetchUsers = async () => {
        const _users = await UserService.getUsers();
        setUsers(_users);
      }
      fetchUsers();
    }

    if(props.seasons) {
      const fetchSeasons = async () => {
        const _seasons = await SeasonService.getSeasons();
        setSeasons(_seasons)
      }

      fetchSeasons();
    }

    if(props.phases) {
      setPhases(crop_phases)
    }
  }, []);

  useEffect(()=>{
    const _farmers_options = farmers?.map((farmer, idx) => {

      // if farmer name is only one word not two - stupid // FIXME
      let farmer_name_avatar = farmer.name?.split(" ").length > 1 ? (farmer.name?.split(" ")[0][0] + farmer.name?.split(" ")[1][0]) : farmer.name[0]

      return {
        key: idx,
        label: (
          <Space direction="horizontal" style={{textAlign: "left"}}>
            <Avatar size="small" style={{ backgroundColor: '#f56a00', fontSize: 11}}>{farmer_name_avatar}</Avatar>
            <Typography.Text>{farmer.name}</Typography.Text>
          </Space>
        ),
        value: {id: farmer.id, name: farmer.name},
        searchText: `${farmer.name || ''}`
      }
    })

    setFarmerOptions(_farmers_options);
  }, [farmers])

  useEffect(()=>{
    if(phases) {
    const _phase_options = Object.keys(phases)?.map((_phase_key, idx) => {
      const phase = phases[_phase_key];
      return {
        key: idx,
        label: (
          <Space direction="horizontal" style={{textAlign: "left"}}>
            <Avatar size="small" style={{ backgroundColor: phase.colors.color, color: phase.colors.textColor, fontSize: 11, fontWeight: "bold"}}>
              {phase.name[0]}
            </Avatar>
            <Typography.Text>
            {t(`components.${phase.name.toLowerCase().replace("/", "").replace("pre-", "")}`)}
            </Typography.Text>
          </Space>
        ),
        value: {name: phase.name, id: _phase_key},
        searchText: `${phase.name || ''} ${t(`components.${phase.name.toLowerCase().replace("/", "").replace("pre-", "")}`)}`
      }
    })
    setPhaseOptions(_phase_options);
  }
  }, [phases, t])

  useEffect(()=>{
    // if the user is invited, don't show them in the list
    const _user_options = users?.filter(u => !u.invited).map((user, idx) => {
      let user_name_avatar = "";
      if (typeof user.name === "string" && user.name.length > 0) {
        const nameParts = user.name.split(" ");
        if (nameParts.length > 1) {
          user_name_avatar = (nameParts[0][0] || "") + (nameParts[1][0] || "");
        } else {
          user_name_avatar = user.name[0];
        }
      }

      return {
        label: (
          <Space direction="horizontal" style={{textAlign: "left"}}>
            <Avatar size="small" style={{ backgroundColor: '#f56a00', fontSize: 11}}>{user_name_avatar}</Avatar>
            <Typography.Text>{user.name}</Typography.Text>
          </Space>
        ),
        value:  {id: user.id, name: user.name},
        key: idx,
        searchText: `${user.name || ''} ${user.email || ''} ${user.phone || ''}`,
      }
    })

    setUserOptions(_user_options);
  }, [users])

  // FIXME - this should update the search criteria when with actual objects of keys, not id, i.e. season_id, user_id etc.
  // useEffect(()=>{
  //   if(props.searchCriteria) {
  //     setSearchCriteria(props.searchCriteria)
  //   }
  // }, [props.searchCriteria])

  useEffect(()=>{
    const _season_options = seasons?.map((season, idx) => {
      return {
        key: idx,
        label: (
          <Space direction="horizontal" style={{textAlign: "left"}}>
            {/* <Avatar shape="square" size="small" style={{ backgroundColor: '#f56a00', fontSize: 11}}>{season.name[0] + season.name[1]}</Avatar> */}
            <Avatar size={"small"} style={{ backgroundColor: season.color }} shape="square" icon={<SunOutlined />} />
            <Typography.Text>{season.name}</Typography.Text>
          </Space>
        ),
        value: {id: season.id, name: season.name},
        searchText: `${season.name || ''}`
      }
    })

    setSeasonOptions(_season_options);
  }, [seasons])

  const clearFarmersCascader = () => {
    let _search_criteria = {...search_criteria}
    _search_criteria.farmer = false
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria)
  }

  const clearUsersCascader = () => {
    let _search_criteria = {...search_criteria}
    _search_criteria.user = false
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria)
  }

  const clearSeasonsCascader = () => {
    let _search_criteria = {...search_criteria}
    _search_criteria.season = false
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria)
  }

   const clearPhasesCascader = () => {
    let _search_criteria = {...search_criteria}
    _search_criteria.phase = false
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria)
  }

  const onSearch = (e) => {
    let _search_criteria = {...search_criteria}
    _search_criteria.search_query = e.target.value.trim();
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria);
  }

  const onChangeCriteria = (criteria, value) => {
    let _search_criteria = {...search_criteria}
    _search_criteria[criteria] = value && value.length > 0 ? value[0]: false
    updateSearchCriteria(_search_criteria);
    //props.onSearch(_search_criteria)
  }

  const setDropdownSearch = (key, value) => {
    setDropdownSearches((prev) => ({ ...prev, [key]: value }));
  };

  const clearDropdownSearch = (key) => {
    setDropdownSearches((prev) => ({ ...prev, [key]: "" }));
  };

  const getMatchingDropdownOptions = (options, key) => {
    const searchValue = (dropdown_searches[key] || "").toLowerCase().trim();
    if (!searchValue) return options || [];
    return (options || []).filter((option) => {
      const searchText = option?.searchText || option?.value?.name || "";
      return searchText.toLowerCase().includes(searchValue);
    });
  };

  const getDropdownOptionsToShow = (options, key) => {
    const searchValue = (dropdown_searches[key] || "").toLowerCase().trim();
    if (!searchValue) return options || [];
    const matches = getMatchingDropdownOptions(options, key);
    return matches.length > 0 ? matches : (options || []);
  };

  const getDropdownSearchMeta = (options, key) => {
    const searchValue = (dropdown_searches[key] || "").toLowerCase().trim();
    if (!searchValue) return { hasInput: false, hasMatches: true };
    const hasMatches = getMatchingDropdownOptions(options, key).length > 0;
    return { hasInput: true, hasMatches };
  };

  const renderDropdownWithSearch = (key, options, menus) => {
    const { hasInput, hasMatches } = getDropdownSearchMeta(options, key);
    const inputBorderColor = hasInput ? (hasMatches ? "#52c41a" : "#ff4d4f") : undefined;
    return (
      <div>
        <div style={{padding: 8}} onClick={(e) => e.stopPropagation()}>
          <Input
            size="small"
            allowClear
            value={dropdown_searches[key]}
            onChange={(e) => setDropdownSearch(key, e.target.value)}
            placeholder={t('general.searchtext')}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ borderColor: inputBorderColor }}
          />
        </div>
        {menus}
      </div>
    );
  };


  return (
    <>
    
    <Space direction="vertical" style={{width: "100%"}}>
      <Space direction="horizontal" style={{width: "100%"}}>
        <div style={{width: "40vw"}}>
          {/**FIXME - Repetition, this should be an atomic component on its own */}
          <div className="search-bar">
            <div className='search-bar-content'>
              <Divider />
              <Input 
                style={{
                  fontSize: 16,
                }}
                onPressEnter={onSearch}
                onChange={props.onChange}
                placeholder={props.placeholder}
                bordered={false} 
                allowClear={true} 
                suffix={<SearchOutlined />} />
              <Divider />
            </div>
          </div>
        </div>
        <div >
          <Space direction="horizontal" className="search-toolbar-buttons" style={{width: "100%"}} split={<Divider orientation="vertical" />}>
            {
            props.users &&
              <div className="search-toolbar-assignees">
                <Tooltip placement="topLeft" title={t('components.assignees')}>
                <Space direction="horizontal">
                  {/* <Typography.Text style={textStyle}>{t('components.assignees')}: </Typography.Text> */}
                  <Cascader allowClear loading={!user_options?.length > 0} onChange={(value) => onChangeCriteria("user", value)} changeOnSelect options={getDropdownOptionsToShow(user_options, "user")} dropdownRender={(menus) => renderDropdownWithSearch("user", user_options, menus)} onOpenChange={(open) => !open && clearDropdownSearch("user")}>
                    
                      <Space direction='horizontal' style={{gap: "0 !important"}}>
                        { !user_options ? 
                          <Spin />
                            :
                          <Avatar.Group shape="circle">
                            {
                              users?.filter(u => !u.invited).slice(0,3).map((user, idx) => {
                                let user_name_avatar = user.name?.split(" ").length > 1 ? (user.name?.split(" ")[0][0] + user.name?.split(" ")[1][0]) : user.name[0]
                                return (
                                  <Avatar key={idx} style={{ backgroundColor: '#f56a00', fontSize: 11}}>{user_name_avatar}</Avatar>
                                )
                              })
                            }
                          </Avatar.Group>
                        }

                        <Typography.Text onClick={clearUsersCascader} style={{padding: 10}}>
                          <ClearOutlined />
                        </Typography.Text>
                        <Typography.Text style={{ margin: "auto", fontSize: 10}}><DownOutlined /></Typography.Text>
                      </Space>
        
                  </Cascader>
                </Space>
                </Tooltip>
              </div>
            }
            {
            props.farmers &&
              <div className="search-toolbar-assignees">
                <Tooltip placement="topLeft" title={t('components.farmers')}>
                <Space direction="horizontal">
                  {/* <Typography.Text style={textStyle} >{t('components.farmers')}: </Typography.Text> */}
                  <Cascader allowClear={{clearIcon: <ClearOutlined />}} onChange={(value) => onChangeCriteria("farmer", value)} options={getDropdownOptionsToShow(farmer_options, "farmer")} dropdownRender={(menus) => renderDropdownWithSearch("farmer", farmer_options, menus)} onOpenChange={(open) => !open && clearDropdownSearch("farmer")}>

                      <Space direction='horizontal'  style={{gap: "0 !important"}}>
                        { !farmer_options ? 
                          <Spin />
                          :
                          <Avatar.Group shape="circle">
                            {
                              farmers?.slice(0,3).map((_farmer, idx) => {
                                let farmer_name_avatar = _farmer.name?.split(" ").length > 1 ? (_farmer.name?.split(" ")[0][0] + _farmer.name?.split(" ")[1][0]) : _farmer.name[0]
                                return (
                                  <Avatar key={`${idx}`} style={{ backgroundColor: '#f56a00', fontSize: 11}}>{farmer_name_avatar}</Avatar>
                                )
                              })
                            }
                          </Avatar.Group>
                        }
                        <Typography.Text onClick={clearFarmersCascader}>
                          <ClearOutlined />
                        </Typography.Text>
                        <Typography.Text style={{ margin: "auto", fontSize: 10}}><DownOutlined /></Typography.Text>
                      </Space>

                  </Cascader>
                </Space>
                </Tooltip>
              </div>
            }

            {
            props.seasons &&
              <div className="search-toolbar-assignees">
                <Tooltip placement="topLeft" title={t('components.seasons')}>
                <Space direction="horizontal">
                  {/* <Typography.Text style={textStyle} >{t('components.farmers')}: </Typography.Text> */}
                  <Cascader allowClear={{clearIcon: <ClearOutlined />}} onChange={(value) => onChangeCriteria("season", value)} options={getDropdownOptionsToShow(season_options, "season")} dropdownRender={(menus) => renderDropdownWithSearch("season", season_options, menus)} onOpenChange={(open) => !open && clearDropdownSearch("season")}>

                      <Space direction='horizontal'  style={{gap: "0 !important"}}>
                        { !seasons ? 
                          <Spin />
                          :
                          <Avatar.Group shape="circle">
                            {
                              seasons?.slice(0,3).map((season, idx) => (
                                <Avatar style={{ backgroundColor: season.color }} shape="square" icon={<SunOutlined />} />
                              ))
                            }
                          </Avatar.Group>
                        }
                        <Typography.Text onClick={clearSeasonsCascader}>
                          <ClearOutlined />
                        </Typography.Text>
                        <Typography.Text style={{ margin: "auto", fontSize: 10}}><DownOutlined /></Typography.Text>
                      </Space>

                  </Cascader>
                </Space>
                </Tooltip>
              </div>
            }

            {
              props.phases &&
              <div className="search-toolbar-assignees">
                <Tooltip placement="topLeft" title={t('components.phase')}>
                <Space direction="horizontal">
                  {/* <Typography.Text style={textStyle} >{t('components.farmers')}: </Typography.Text> */}
                  <Cascader allowClear={{clearIcon: <ClearOutlined />}} onChange={(value) => onChangeCriteria("phase", value)} options={getDropdownOptionsToShow(phase_options, "phase")} dropdownRender={(menus) => renderDropdownWithSearch("phase", phase_options, menus)} onOpenChange={(open) => !open && clearDropdownSearch("phase")}>

                      <Space direction='horizontal'  style={{gap: "0 !important"}}>
                        { !phases ? 
                          <Spin />
                          :
                          <Avatar.Group shape="circle">
                            {
                              Object.keys(phases).slice(0,3).map((phase, idx) => {
                                let _phase = phases[phase]

                                return (<Avatar key={`${idx}`} style={{ backgroundColor: _phase.colors.color, fontWeight: "bolder", color: _phase.colors.textColor }} shape="square" icon={t(`components.${_phase.name.toLowerCase().replace("/", "").replace("pre-", "")}`)[0]} />)
                              })
                            }
                          </Avatar.Group>
                        }
                        <Typography.Text onClick={clearPhasesCascader}>
                          <ClearOutlined />
                        </Typography.Text>
                        <Typography.Text style={{ margin: "auto", fontSize: 10 }}><DownOutlined /></Typography.Text>
                      </Space>

                  </Cascader>
                </Space>
                </Tooltip>
              </div>
            }

            <div className="search-toolbar-button">
            </div>
          </Space>
        </div>
      </Space>
      <Space direction="horizontal" style={{width: "100%"}}>

        { props.page &&
          <Tag closable onClose={() => props.onPageChange({page: 1, pageSize: 5})} >
            {t('general.page')} {props.page.page}
          </Tag>

        }
        {
          search_criteria && Object.keys(search_criteria).map(k => {
            return (
              search_criteria[k] && 
              <Tag closable onClose={() => onChangeCriteria(k, false)} >
                {k === "phase" ? t(`components.${search_criteria[k].name.toLowerCase().replace("/", "").replace("pre-", "")}`) : search_criteria[k].name || search_criteria[k]}
              </Tag>
            )
          })
        }    
       </Space>
      </Space>
    </>
  )
}

export default SearchToolbar;
